<template>
  <div class="admin-view">
    <!-- Dateline / broadsheet header -->
    <header class="dash-header">
      <div class="dash-header__inner">
        <div class="dash-header__titles">
          <p class="dash-header__eyebrow eyebrow">Administration &mdash; Desk Edition</p>
          <h1 class="dash-header__title display-md">Admin Dashboard</h1>
        </div>
        <div v-if="user" class="dash-header__user">
          <span class="dash-header__username">{{ user.full_name || user.email }}</span>
          <span class="dash-header__role">Admin</span>
        </div>
      </div>
      <div class="dash-header__dateline dateline">
        <span>Volume I &middot; Operations</span>
        <span
          ><span class="dateline__dot">♦</span>{{ formattedDate
          }}<span class="dateline__dot">♦</span>Pressbox Console</span
        >
        <span>Status &mdash; Live</span>
      </div>
    </header>

    <!-- Access denied for non-superusers -->
    <div v-if="user && !user.is_superuser" class="access-denied">
      <div class="access-denied__inner paper-card">
        <p class="access-denied__eyebrow eyebrow">Credential Rejected</p>
        <p class="access-denied__title display-md">Access Denied</p>
        <p class="access-denied__message">This area is restricted to superusers.</p>
      </div>
    </div>

    <template v-else-if="user && user.is_superuser">
      <!-- ═══════ I. Job Controls ═══════ -->
      <section class="panel" aria-label="Job Controls">
        <div class="panel__inner">
          <div class="panel__header">
            <div class="section-number">I</div>
            <div class="panel__titles">
              <p class="eyebrow">Trigger &mdash; Manual Operations</p>
              <h2 class="panel__heading display-md">Job Controls</h2>
            </div>
          </div>
          <hr class="rule rule--double" />

          <div class="jobs paper-card">
            <div class="jobs__body">
              <div class="jobs__inputs">
                <div class="field">
                  <label class="field__label" for="job-years">Year Range</label>
                  <input
                    id="job-years"
                    v-model="jobYears"
                    type="text"
                    class="field__input"
                    placeholder="2024 or 2020-2024"
                    aria-describedby="job-years-hint"
                  />
                  <span id="job-years-hint" class="field__hint"
                    >Single year or start&ndash;end range</span
                  >
                </div>
                <div v-if="showIngestOptions" class="field">
                  <label class="field__label" for="ingest-source">Source</label>
                  <select id="ingest-source" v-model="ingestSource" class="field__input">
                    <option value="retrosheet">Retrosheet</option>
                    <option value="mlbam">MLBAM</option>
                  </select>
                </div>
              </div>
              <div class="jobs__actions">
                <button
                  class="btn btn--accent btn-press"
                  :disabled="!jobYears.trim() || jobLoading"
                  @click="triggerJob('enrich')"
                >
                  Enrich
                </button>
                <button
                  class="btn btn--accent btn-press"
                  :disabled="!jobYears.trim() || jobLoading"
                  @click="triggerJob('aggregate')"
                >
                  Aggregate
                </button>
                <button
                  class="btn btn--gold btn-press btn-press--ghost"
                  :disabled="!jobYears.trim() || jobLoading"
                  @click="showIngestOptions = !showIngestOptions"
                >
                  Ingest {{ showIngestOptions ? '...' : '' }}
                </button>
                <button
                  v-if="showIngestOptions"
                  class="btn btn--accent btn-press"
                  :disabled="!jobYears.trim() || jobLoading"
                  @click="triggerJob('ingest')"
                >
                  Run Ingest
                </button>
                <button
                  class="btn btn--dim btn-press btn-press--ghost"
                  :disabled="jobLoading"
                  @click="triggerReadinessCheck"
                >
                  Readiness Check
                </button>
                <button
                  class="btn btn--accent btn-press"
                  :disabled="jobLoading"
                  @click="triggerPullOddsJob"
                >
                  Pull Odds
                </button>
                <button
                  class="btn btn--accent btn-press"
                  :disabled="jobLoading"
                  @click="triggerScheduleImportJob"
                >
                  Import Schedule
                </button>
              </div>

              <hr class="jobs__divider" />

              <!-- Simulate Season -->
              <div class="jobs__inputs jobs__inputs--sim">
                <div class="field">
                  <label class="field__label" for="sim-year">Season Year</label>
                  <input
                    id="sim-year"
                    v-model.number="simYear"
                    type="number"
                    class="field__input field__input--narrow"
                    placeholder="2025"
                    min="2000"
                    max="2100"
                  />
                </div>
                <div class="field field--inline">
                  <input
                    id="sim-force"
                    v-model="simForce"
                    type="checkbox"
                    class="field__checkbox"
                  />
                  <label class="field__label field__label--checkbox" for="sim-force">
                    Force re-sim
                  </label>
                </div>
              </div>
              <div class="jobs__actions">
                <button
                  class="btn btn--accent btn-press"
                  :disabled="!simYear || jobLoading"
                  @click="triggerSimulateSeasonJob"
                >
                  Simulate Season
                </button>
              </div>

              <!-- Toast / job status -->
              <div v-if="jobMessage" class="jobs__status" :class="jobMessageClass">
                <p class="jobs__message">{{ jobMessage }}</p>
                <div v-if="activeJobIds.length > 0" class="jobs__polling">
                  <div v-for="jid in activeJobIds" :key="jid" class="jobs__job">
                    <span class="jobs__job-id">{{ jid.slice(0, 12) }}&hellip;</span>
                    <span
                      class="jobs__job-status"
                      :class="'jobs__job-status--' + (polledStatuses[jid] || 'unknown')"
                    >
                      {{ polledStatuses[jid] || 'polling…' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══════ II. Queue Monitor ═══════ -->
      <section class="panel" aria-label="Queue Monitor">
        <div class="panel__inner">
          <div class="panel__header">
            <div class="section-number">II</div>
            <div class="panel__titles">
              <p class="eyebrow">Live Wire &mdash; Background Workers</p>
              <h2 class="panel__heading display-md">Queue Monitor</h2>
            </div>
          </div>
          <hr class="rule rule--double" />

          <div class="queue-header">
            <div class="queue-counts">
              <span class="queue-count queue-count--queued">
                <span class="queue-count__num numeric">{{ queueStats.queued }}</span>
                <span class="queue-count__label">Queued</span>
              </span>
              <span class="queue-count queue-count--active">
                <span class="queue-count__num numeric">{{ queueStats.active }}</span>
                <span class="queue-count__label">Active</span>
              </span>
              <span class="queue-count queue-count--scheduled">
                <span class="queue-count__num numeric">{{ queueStats.scheduled }}</span>
                <span class="queue-count__label">Scheduled</span>
              </span>
              <span class="queue-count">
                <span class="queue-count__num numeric">{{ queueStats.worker_count }}</span>
                <span class="queue-count__label">Workers</span>
              </span>
            </div>
          </div>

          <!-- Filter chips -->
          <div v-if="queueStats.jobs.length > 0" class="job-filters">
            <span class="job-filters__label">Status</span>
            <button
              v-for="s in JOB_STATUSES"
              :key="s"
              class="filter-chip"
              :class="[
                `filter-chip--status-${s}`,
                { 'filter-chip--active': visibleStatuses.has(s) }
              ]"
              @click="toggleStatus(s)"
            >
              {{ s }}
            </button>
            <span class="filter-separator"></span>
            <span class="job-filters__label">Queue</span>
            <button
              v-for="q in QUEUE_NAMES"
              :key="q"
              class="filter-chip filter-chip--queue"
              :class="{ 'filter-chip--active': visibleQueues.has(q) }"
              @click="toggleQueue(q)"
            >
              {{ q }}
            </button>
          </div>

          <div v-if="filteredJobs.length > 0" class="table-wrap paper-card">
            <table class="data-table" aria-label="Job history">
              <thead>
                <tr>
                  <th>Function</th>
                  <th>Status</th>
                  <th>Queue</th>
                  <th>Job ID</th>
                  <th class="num sortable" @click="setSort('queued_at')">
                    Queued <span class="sort-icon">{{ sortIcon('queued_at') }}</span>
                  </th>
                  <th class="num sortable" @click="setSort('scheduled_at')">
                    Scheduled <span class="sort-icon">{{ sortIcon('scheduled_at') }}</span>
                  </th>
                  <th class="num sortable" @click="setSort('started_at')">
                    Started <span class="sort-icon">{{ sortIcon('started_at') }}</span>
                  </th>
                  <th class="num sortable" @click="setSort('completed_at')">
                    Completed <span class="sort-icon">{{ sortIcon('completed_at') }}</span>
                  </th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="job in filteredJobs" :key="job.job_id">
                  <td class="mono">{{ formatFunctionName(job.function) }}</td>
                  <td>
                    <span class="status-badge" :class="jobStatusClass(job.status)">
                      {{ job.status }}
                    </span>
                  </td>
                  <td class="mono dim">{{ job.queue || 'default' }}</td>
                  <td class="mono dim">{{ job.job_id }}</td>
                  <td class="num mono dim">{{ formatTs(job.queued_at) }}</td>
                  <td class="num mono dim">{{ formatTs(job.scheduled_at) }}</td>
                  <td class="num mono dim">{{ formatTs(job.started_at) }}</td>
                  <td class="num mono dim">{{ formatTs(job.completed_at) }}</td>
                  <td class="mono error-cell">{{ job.error ? job.error.slice(0, 60) : '' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-state">No jobs match the current filter.</p>
        </div>
      </section>

      <!-- ═══════ III. Data Readiness ═══════ -->
      <section class="panel" aria-label="Data Readiness">
        <div class="panel__inner">
          <div class="panel__header">
            <div class="section-number">III</div>
            <div class="panel__titles">
              <p class="eyebrow">Contents &mdash; Season-by-Season</p>
              <h2 class="panel__heading display-md">Data Readiness</h2>
            </div>
          </div>
          <hr class="rule rule--double" />

          <div class="readiness-header">
            <div class="readiness-controls">
              <div class="field field--inline">
                <label class="field__label" for="readiness-years">Years</label>
                <input
                  id="readiness-years"
                  v-model="readinessYears"
                  type="text"
                  class="field__input field__input--narrow"
                  placeholder="2020-2024"
                  @keyup.enter="fetchReadiness"
                />
              </div>
              <button
                class="btn btn--small btn-press btn-press--ghost"
                :disabled="readinessLoading"
                @click="fetchReadiness"
              >
                {{ readinessLoading ? 'Loading…' : 'Fetch' }}
              </button>
            </div>
          </div>

          <div v-if="readinessError" class="alert alert--error">
            {{ readinessError }}
          </div>

          <div v-if="seasons.length > 0" class="table-wrap paper-card">
            <table class="data-table" aria-label="Season data readiness">
              <thead>
                <tr>
                  <th>Year</th>
                  <th class="num">Games</th>
                  <th class="num">PAs</th>
                  <th class="num">Bat Game</th>
                  <th class="num">Bat Season</th>
                  <th class="num">Pit Game</th>
                  <th class="num">Pit Season</th>
                  <th class="num">Enriched</th>
                  <th class="num">Tier 1</th>
                  <th>Gaps</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in seasons" :key="s.year">
                  <td class="mono year-cell">{{ s.year }}</td>
                  <td class="num mono">{{ s.games.toLocaleString() }}</td>
                  <td class="num mono">{{ s.plate_appearances.toLocaleString() }}</td>
                  <td class="num mono">{{ s.batting_game_rows.toLocaleString() }}</td>
                  <td class="num mono">{{ s.batting_season_rows.toLocaleString() }}</td>
                  <td class="num mono">{{ s.pitching_game_rows.toLocaleString() }}</td>
                  <td class="num mono">{{ s.pitching_season_rows.toLocaleString() }}</td>
                  <td class="num mono">
                    <span :class="enrichmentClass(s)">
                      {{ enrichmentLabel(s) }}
                    </span>
                  </td>
                  <td class="num mono">
                    <span :class="tier1Class(s)">
                      {{ tier1Label(s) }}
                    </span>
                  </td>
                  <td>
                    <span class="gaps">
                      <span v-if="!s.has_roe_data" class="gap-tag gap-tag--red">ROE</span>
                      <span v-if="!s.has_error_position" class="gap-tag gap-tag--yellow"
                        >ErrPos</span
                      >
                      <span v-if="!s.has_hit_location" class="gap-tag gap-tag--yellow">HitLoc</span>
                      <span
                        v-if="s.has_roe_data && s.has_error_position && s.has_hit_location"
                        class="gap-tag gap-tag--green"
                        >None</span
                      >
                    </span>
                  </td>
                  <td>
                    <span class="status-badge" :class="statusClass(s)">
                      {{ statusLabel(s) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-else-if="!readinessLoading && !readinessError" class="empty-state">
            Enter a year range and click Fetch to load readiness data.
          </p>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, reactive } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { dataApi } from '@/api/data'
import { adminApi } from '@/api/admin'
import type { SeasonReadiness } from '@/api/data'
import type { JobSummary, QueueStatsResponse } from '@/api/admin'
import type { ApiError } from '@/types/api'

const { user } = useAuth()

const formattedDate = new Date().toLocaleDateString('en-US', {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})

// ---------------------------------------------------------------------------
// Queue Monitor
// ---------------------------------------------------------------------------

const EMPTY_QUEUE_STATS: QueueStatsResponse = {
  queued: 0,
  active: 0,
  scheduled: 0,
  worker_count: 0,
  jobs: []
}
const queueStats = ref<QueueStatsResponse>({ ...EMPTY_QUEUE_STATS })
let queuePollTimer: ReturnType<typeof setInterval> | null = null

async function refreshQueueStats(): Promise<void> {
  try {
    queueStats.value = await adminApi.getQueueStats()
  } catch {
    // silently ignore — counts stay at last known value
  }
}

function startQueuePolling(): void {
  refreshQueueStats()
  queuePollTimer = setInterval(refreshQueueStats, 5000)
}

function stopQueuePolling(): void {
  if (queuePollTimer) {
    clearInterval(queuePollTimer)
    queuePollTimer = null
  }
}

// SAQ timestamps (queued_at, started_at, etc.) are Unix epoch milliseconds.
// new Date(ms) is correct — do not multiply by 1000.
function formatTs(ts: number | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

function formatFunctionName(fn: string): string {
  return fn.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function jobStatusClass(s: string): string {
  if (s === 'complete') return 'status-badge--green'
  if (s === 'active') return 'status-badge--ox'
  if (s === 'scheduled') return 'status-badge--gold'
  if (s === 'retrying') return 'status-badge--gold'
  if (s === 'failed' || s === 'aborted') return 'status-badge--red'
  return 'status-badge--dim'
}

// ---------------------------------------------------------------------------
// Job table filtering + sorting
// ---------------------------------------------------------------------------

const JOB_STATUSES = [
  'active',
  'queued',
  'scheduled',
  'complete',
  'failed',
  'swept',
  'aborted'
] as const

const STATUS_PRIORITY: Record<string, number> = {
  active: 0,
  queued: 1,
  scheduled: 2,
  complete: 3,
  failed: 4,
  aborted: 4,
  swept: 5
}

const visibleStatuses = reactive(new Set<string>(['active', 'queued', 'scheduled', 'failed']))

function toggleStatus(s: string): void {
  if (visibleStatuses.has(s)) visibleStatuses.delete(s)
  else visibleStatuses.add(s)
}

const QUEUE_NAMES = ['default', 'ingestion'] as const
const visibleQueues = reactive(new Set<string>(['default', 'ingestion']))

function toggleQueue(q: string): void {
  if (visibleQueues.has(q)) visibleQueues.delete(q)
  else visibleQueues.add(q)
}

type SortKey = keyof Pick<JobSummary, 'queued_at' | 'scheduled_at' | 'started_at' | 'completed_at'>
const sortKey = ref<SortKey | null>(null)
const sortAsc = ref(false)

function setSort(key: SortKey): void {
  if (sortKey.value === key) sortAsc.value = !sortAsc.value
  else {
    sortKey.value = key
    sortAsc.value = false
  }
}

function sortIcon(key: SortKey): string {
  if (sortKey.value !== key) return '↕'
  return sortAsc.value ? '↑' : '↓'
}

const filteredJobs = computed(() => {
  const jobs = queueStats.value.jobs.filter(
    j => visibleStatuses.has(j.status) && visibleQueues.has(j.queue || 'default')
  )
  return [...jobs].sort((a, b) => {
    if (sortKey.value) {
      const av = a[sortKey.value] ?? 0
      const bv = b[sortKey.value] ?? 0
      return sortAsc.value ? av - bv : bv - av
    }
    // default: status priority, then started_at desc
    const pd = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status]
    if (pd !== 0) return pd
    return (b.started_at ?? b.queued_at ?? 0) - (a.started_at ?? a.queued_at ?? 0)
  })
})

onMounted(startQueuePolling)

// ---------------------------------------------------------------------------
// Data Readiness
// ---------------------------------------------------------------------------

const readinessYears = ref<string>('2020-2024')
const readinessLoading = ref(false)
const readinessError = ref<string | null>(null)
const seasons = ref<SeasonReadiness[]>([])

async function fetchReadiness(): Promise<void> {
  const years = readinessYears.value.trim()
  if (!years) return

  readinessLoading.value = true
  readinessError.value = null
  try {
    const response = await dataApi.getReadiness(years)
    seasons.value = response.seasons
  } catch (err: unknown) {
    const apiErr = err as ApiError
    readinessError.value = apiErr.detail || 'Failed to fetch readiness data'
    seasons.value = []
  } finally {
    readinessLoading.value = false
  }
}

function enrichmentLabel(s: SeasonReadiness): string {
  if (s.batting_game_rows === 0 && s.pitching_game_rows === 0) return '--'
  const batPct = s.batting_game_rows > 0 ? (s.enriched_batting / s.batting_game_rows) * 100 : 100
  const pitPct = s.pitching_game_rows > 0 ? (s.enriched_pitching / s.pitching_game_rows) * 100 : 100
  const avg = (batPct + pitPct) / 2
  const pct = `${Math.round(avg)}%`
  return s.enrichment_source_limited ? `${pct}*` : pct
}

function enrichmentClass(s: SeasonReadiness): string {
  if (s.batting_game_rows === 0 && s.pitching_game_rows === 0) return 'dim'
  if (!s.needs_enrichment) return 'text-green'
  const batPct = s.batting_game_rows > 0 ? s.enriched_batting / s.batting_game_rows : 1
  const pitPct = s.pitching_game_rows > 0 ? s.enriched_pitching / s.pitching_game_rows : 1
  const avg = (batPct + pitPct) / 2
  return avg > 0.5 ? 'text-yellow' : 'text-red'
}

function statusLabel(s: SeasonReadiness): string {
  if (s.games === 0) return 'Empty'
  if (s.needs_enrichment && s.needs_aggregation) return 'Needs Work'
  if (s.needs_enrichment) return 'Enrich'
  if (s.needs_aggregation) return 'Aggregate'
  return s.enrichment_source_limited ? 'Ready*' : 'Ready'
}

function statusClass(s: SeasonReadiness): string {
  if (s.games === 0) return 'status-badge--dim'
  if (s.needs_enrichment && s.needs_aggregation) return 'status-badge--red'
  if (s.needs_enrichment || s.needs_aggregation) return 'status-badge--yellow'
  return 'status-badge--green'
}

function tier1Label(s: SeasonReadiness): string {
  const bat = s.tier1_batting
  const pit = s.tier1_pitching
  if (bat === 0 && pit === 0) return '--'
  return `${bat + pit}`
}

function tier1Class(s: SeasonReadiness): string {
  const total = s.tier1_batting + s.tier1_pitching
  return total === 0 ? 'dim' : 'text-green'
}

// ---------------------------------------------------------------------------
// Job Triggers
// ---------------------------------------------------------------------------

const jobYears = ref<string>('')
const jobLoading = ref(false)
const jobMessage = ref<string | null>(null)
const jobError = ref(false)
const activeJobIds = ref<string[]>([])
const polledStatuses = ref<Record<string, string>>({})
const showIngestOptions = ref(false)
const ingestSource = ref('retrosheet')
const simYear = ref<number | null>(new Date().getFullYear())
const simForce = ref(true)

let pollTimers: ReturnType<typeof setInterval>[] = []

const jobMessageClass = computed((): string => {
  if (jobError.value) return 'jobs__status--error'
  return 'jobs__status--success'
})

async function triggerReadinessCheck(): Promise<void> {
  jobLoading.value = true
  jobMessage.value = null
  jobError.value = false
  activeJobIds.value = []

  try {
    const result = await adminApi.triggerReadinessCheck(jobYears.value.trim() || undefined)
    jobMessage.value = result.message
    const id = result.job_id
    if (id && id !== 'already-queued') {
      activeJobIds.value = [id]
      startPolling([id])
    }
  } catch (err: unknown) {
    const apiErr = err as ApiError
    jobMessage.value = apiErr.detail || 'Failed to trigger readiness check'
    jobError.value = true
  } finally {
    jobLoading.value = false
  }
}

async function triggerPullOddsJob(): Promise<void> {
  jobLoading.value = true
  jobMessage.value = null
  jobError.value = false
  activeJobIds.value = []
  try {
    const result = await adminApi.triggerPullOdds()
    jobMessage.value = result.message
    const id = result.job_id
    if (id && id !== 'already-queued') {
      activeJobIds.value = [id]
      startPolling([id])
    }
  } catch (err: unknown) {
    const apiErr = err as ApiError
    jobMessage.value = apiErr.detail || 'Failed to trigger odds pull'
    jobError.value = true
  } finally {
    jobLoading.value = false
  }
}

async function triggerScheduleImportJob(): Promise<void> {
  jobLoading.value = true
  jobMessage.value = null
  jobError.value = false
  activeJobIds.value = []
  try {
    const result = await adminApi.triggerScheduleImport(new Date().getFullYear())
    jobMessage.value = result.message
    const id = result.job_id
    if (id && id !== 'already-queued') {
      activeJobIds.value = [id]
      startPolling([id])
    }
  } catch (err: unknown) {
    const apiErr = err as ApiError
    jobMessage.value = apiErr.detail || 'Failed to trigger schedule import'
    jobError.value = true
  } finally {
    jobLoading.value = false
  }
}

async function triggerSimulateSeasonJob(): Promise<void> {
  if (!simYear.value) return

  jobLoading.value = true
  jobMessage.value = null
  jobError.value = false
  activeJobIds.value = []

  try {
    const result = await adminApi.triggerSimulateSeason(simYear.value, 1000, simForce.value)
    jobMessage.value = result.message
    const id = result.job_id
    if (id && id !== 'already-queued') {
      activeJobIds.value = [id]
      startPolling([id])
    }
  } catch (err: unknown) {
    const apiErr = err as ApiError
    jobMessage.value = apiErr.detail || 'Failed to trigger season simulation'
    jobError.value = true
  } finally {
    jobLoading.value = false
  }
}

async function triggerJob(type: 'enrich' | 'aggregate' | 'ingest'): Promise<void> {
  const years = jobYears.value.trim()
  if (!years) return

  jobLoading.value = true
  jobMessage.value = null
  jobError.value = false
  activeJobIds.value = []

  try {
    let result: { job_id: string; message: string }
    if (type === 'enrich') {
      result = await adminApi.triggerEnrich(years)
    } else if (type === 'aggregate') {
      result = await adminApi.triggerAggregate(years)
    } else {
      result = await adminApi.triggerIngest(ingestSource.value, years)
    }

    jobMessage.value = result.message
    const ids = result.job_id.split(',').filter(id => id && id !== 'already-queued')
    activeJobIds.value = ids
    if (ids.length > 0) {
      startPolling(ids)
    }
  } catch (err: unknown) {
    const apiErr = err as ApiError
    jobMessage.value = apiErr.detail || `Failed to trigger ${type} job`
    jobError.value = true
  } finally {
    jobLoading.value = false
  }
}

function startPolling(ids: string[]): void {
  stopPolling()
  for (const id of ids) {
    polledStatuses.value[id] = 'queued'
    const timer = setInterval(async () => {
      try {
        const status = await adminApi.getJobStatus(id)
        polledStatuses.value[id] = status.status
        if (status.status === 'complete' || status.status === 'failed') {
          clearInterval(timer)
          pollTimers = pollTimers.filter(t => t !== timer)
        }
      } catch {
        polledStatuses.value[id] = 'error'
        clearInterval(timer)
        pollTimers = pollTimers.filter(t => t !== timer)
      }
    }, 3000)
    pollTimers.push(timer)
  }
}

function stopPolling(): void {
  for (const timer of pollTimers) {
    clearInterval(timer)
  }
  pollTimers = []
}

onUnmounted(() => {
  stopPolling()
  stopQueuePolling()
})
</script>

<style scoped>
/* ═════════════════════════════════════════════════════════
   ADMIN VIEW — Pressbox aesthetic
   Cream paper, ink navy, ox-blood accents.
   ═════════════════════════════════════════════════════════ */
.admin-view {
  min-height: 100vh;
  background: transparent;
  color: var(--ink);
  font-family: var(--font-editorial);
  padding-top: 3.5rem;
}

/* ─── Header / Dateline ─────────────────────────────────── */
.dash-header {
  padding: 2rem 1.5rem 0;
  border-bottom: var(--rule-thin) solid var(--ink);
}

.dash-header__inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  padding-bottom: 1rem;
}

.dash-header__eyebrow {
  margin-bottom: 0.5rem;
}

.dash-header__title {
  margin: 0;
}

.dash-header__user {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  text-align: right;
}

.dash-header__username {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: 0.9rem;
  color: var(--ink);
}

.dash-header__role {
  font-family: var(--font-ui);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ox);
}

.dash-header__dateline {
  max-width: 1200px;
  margin: 0 auto;
  border-bottom: none;
}

/* ─── Access Denied ─────────────────────────────────────── */
.access-denied {
  padding: 4rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.access-denied__inner {
  padding: 2rem;
  border-left: var(--rule-thick) solid var(--ox);
}

.access-denied__eyebrow {
  color: var(--ox);
  margin-bottom: 0.75rem;
}

.access-denied__title {
  color: var(--ox);
  margin-bottom: 0.75rem;
}

.access-denied__message {
  font-family: var(--font-editorial);
  font-size: 1rem;
  color: var(--ink-medium);
}

/* ─── Panel ─────────────────────────────────────────────── */
.panel {
  padding: 3rem 1.5rem;
  border-bottom: var(--rule-thin) solid var(--paper-edge);
}

.panel__inner {
  max-width: 1200px;
  margin: 0 auto;
}

.panel__header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.panel__titles {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.panel__heading {
  margin: 0;
  letter-spacing: -0.015em;
}

.rule {
  margin: 1rem 0 1.75rem;
  border-top-color: var(--ink);
}

.rule--double {
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  height: 4px;
}

/* ─── Form fields ───────────────────────────────────────── */
.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field--inline {
  flex-direction: row;
  align-items: center;
  gap: 0.6rem;
}

.field__label {
  font-family: var(--font-ui);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-medium);
}

.field__input {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: 0.9rem;
  padding: 0.55rem 0.8rem;
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  color: var(--ink);
  border-radius: 0;
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  min-width: 200px;
}

.field__input:focus {
  border-color: var(--ox);
  box-shadow: 2px 2px 0 var(--ink);
}

.field__input--narrow {
  min-width: 140px;
  max-width: 180px;
}

.field__hint {
  font-family: var(--font-ui);
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

select.field__input {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%230D1B2F' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
  padding-right: 2rem;
}

/* ─── Buttons (keep class names, swap visuals) ──────────── */
.btn {
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0.65rem 1.2rem;
  border: var(--rule-thin) solid var(--ink);
  background: var(--paper);
  color: var(--ink);
  border-radius: 0;
  cursor: pointer;
  box-shadow: var(--shadow-ink);
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease,
    background 0.15s ease,
    color 0.15s ease;
  white-space: nowrap;
}

.btn:hover:not(:disabled) {
  background: var(--ink);
  color: var(--paper);
  transform: translate(-1px, -1px);
  box-shadow: 3px 3px 0 var(--ink);
}

.btn:active:not(:disabled) {
  transform: translate(1px, 1px);
  box-shadow: 0 0 0 var(--ink);
}

.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

.btn--accent {
  background: var(--ink);
  color: var(--paper);
}

.btn--accent:hover:not(:disabled) {
  background: var(--ox);
  color: var(--paper);
}

.btn--gold {
  background: var(--paper);
  color: var(--ink);
  border-color: var(--ink);
}

.btn--gold:hover:not(:disabled) {
  background: var(--gold);
  color: var(--ink);
}

.btn--small {
  padding: 0.5rem 1rem;
  font-size: 0.68rem;
}

.btn--dim {
  background: var(--paper);
  color: var(--ink-medium);
}

.btn--dim:hover:not(:disabled) {
  background: var(--ink);
  color: var(--paper);
}

/* ─── Jobs panel (paper card) ───────────────────────────── */
.jobs {
  padding: 0;
}

.jobs__body {
  padding: 1.5rem;
}

.jobs__inputs {
  display: flex;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.jobs__divider {
  border: none;
  border-top: var(--rule-thin) dashed var(--paper-edge);
  margin: 1.5rem 0 1.25rem;
}

.field__checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: var(--ox);
  cursor: pointer;
  flex-shrink: 0;
}

.field__label--checkbox {
  cursor: pointer;
}

.jobs__actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.jobs__status {
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  font-family: var(--font-data);
  font-size: 0.82rem;
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
}

.jobs__status--success {
  border-left: var(--rule-thick) solid var(--ivy);
  color: var(--ivy);
}

.jobs__status--error {
  border-left: var(--rule-thick) solid var(--ox);
  color: var(--ox);
}

.jobs__message {
  margin-bottom: 0.5rem;
  color: var(--ink);
}

.jobs__polling {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.jobs__job {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.jobs__job-id {
  color: var(--ink-faint);
  font-size: 0.75rem;
  font-family: var(--font-data);
}

.jobs__job-status {
  font-family: var(--font-ui);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.jobs__job-status--queued,
.jobs__job-status--active {
  color: var(--ox);
}

.jobs__job-status--complete {
  color: var(--ivy);
}

.jobs__job-status--failed,
.jobs__job-status--error {
  color: var(--ox);
}

/* ─── Readiness controls ────────────────────────────────── */
.readiness-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.readiness-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* ─── Alert ─────────────────────────────────────────────── */
.alert {
  padding: 1rem 1.25rem;
  font-family: var(--font-data);
  font-size: 0.82rem;
  margin-bottom: 1.25rem;
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
}

.alert--error {
  border-left: var(--rule-thick) solid var(--ox);
  color: var(--ox);
}

/* ─── Data Table ────────────────────────────────────────── */
.table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--paper-deep);
  padding: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
  white-space: nowrap;
  font-family: var(--font-editorial);
}

.data-table th {
  font-family: var(--font-ui);
  font-size: 0.63rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink);
  padding: 0.8rem 0.9rem;
  text-align: left;
  border-bottom: var(--rule-med) solid var(--ink);
  background: var(--paper-deep);
  position: sticky;
  top: 0;
}

.data-table th.num {
  text-align: right;
}

.data-table td {
  padding: 0.6rem 0.9rem;
  border-bottom: 1px dashed var(--paper-edge);
  color: var(--ink);
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table td.num,
.data-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.data-table td.mono,
.data-table .mono {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
}

.data-table .year-cell {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 600;
  font-size: 1rem;
  color: var(--ox);
}

.data-table tbody tr {
  transition: background 0.15s;
}

.data-table tbody tr:hover {
  background: var(--ink-10);
}

/* ─── Status & Color helpers ────────────────────────────── */
.text-green {
  color: var(--ivy);
  font-weight: 600;
}

.text-yellow {
  color: var(--gold);
  font-weight: 600;
}

.text-red {
  color: var(--ox);
  font-weight: 600;
}

.dim {
  color: var(--ink-faint);
}

.status-badge {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 0.25rem 0.55rem;
  white-space: nowrap;
  border: var(--rule-thin) solid currentColor;
}

.status-badge--green {
  color: var(--ivy);
  background: rgba(46, 83, 57, 0.08);
}

.status-badge--yellow {
  color: var(--gold);
  background: rgba(184, 138, 62, 0.1);
}

.status-badge--red {
  color: var(--ox);
  background: var(--ox-10);
}

.status-badge--ox {
  color: var(--paper);
  background: var(--ox);
  border-color: var(--ox);
}

.status-badge--gold {
  color: var(--gold);
  background: rgba(184, 138, 62, 0.1);
}

.status-badge--dim {
  color: var(--ink-faint);
  background: transparent;
  border-color: var(--paper-edge);
}

.gaps {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.gap-tag {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.15rem 0.4rem;
  border: var(--rule-thin) solid currentColor;
}

.gap-tag--green {
  color: var(--ivy);
  background: rgba(46, 83, 57, 0.08);
}

.gap-tag--yellow {
  color: var(--gold);
  background: rgba(184, 138, 62, 0.1);
}

.gap-tag--red {
  color: var(--ox);
  background: var(--ox-10);
}

.empty-state {
  color: var(--ink-faint);
  font-family: var(--font-editorial);
  font-style: italic;
  font-size: 0.95rem;
  padding: 2rem 0;
  text-align: center;
  border-top: 1px dashed var(--paper-edge);
  border-bottom: 1px dashed var(--paper-edge);
}

/* ─── Queue Monitor ─────────────────────────────────────── */
.queue-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: var(--paper-deep);
  border: var(--rule-thin) solid var(--ink);
}

.job-filters {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1rem;
}

.job-filters__label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-right: 0.15rem;
}

.filter-chip {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.3rem 0.7rem;
  border: var(--rule-thin) solid var(--paper-edge);
  background: transparent;
  color: var(--ink-faint);
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
  border-radius: 0;
}

.filter-chip:hover {
  border-color: var(--ink);
  color: var(--ink);
}

.filter-chip--active {
  color: var(--ink);
  border-color: var(--ink);
  background: var(--paper);
}

.filter-chip--active.filter-chip--status-queued {
  color: var(--ink);
  border-color: var(--ink-medium);
}

.filter-chip--active.filter-chip--status-scheduled {
  color: var(--gold);
  border-color: var(--gold);
}

.filter-chip--active.filter-chip--status-active {
  color: var(--paper);
  background: var(--ox);
  border-color: var(--ox);
}

.filter-chip--active.filter-chip--status-complete {
  color: var(--ivy);
  border-color: var(--ivy);
}

.filter-chip--active.filter-chip--status-failed,
.filter-chip--active.filter-chip--status-aborted {
  color: var(--ox);
  border-color: var(--ox);
}

.filter-separator {
  width: 1px;
  height: 1.2em;
  background: var(--paper-edge);
  margin: 0 0.5rem;
}

.filter-chip--active.filter-chip--queue {
  color: var(--paper);
  background: var(--ox);
  border-color: var(--ox);
}

/* ─── Sortable column headers ───────────────────────────── */
.data-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.data-table th.sortable:hover {
  color: var(--ox);
}

.sort-icon {
  opacity: 0.6;
  font-size: 0.6rem;
  margin-left: 0.2rem;
}

.queue-counts {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.queue-count {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
}

.queue-count__num {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 700;
  font-size: 2rem;
  color: var(--ink);
  line-height: 0.95;
  letter-spacing: -0.02em;
}

.queue-count__label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

.queue-count--active .queue-count__num {
  color: var(--ox);
}

.queue-count--scheduled .queue-count__num {
  color: var(--gold);
}

.error-cell {
  color: var(--ox);
  font-size: 0.75rem;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ─── Responsive ────────────────────────────────────────── */
@media (max-width: 768px) {
  .dash-header__inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .dash-header__user {
    align-items: flex-start;
    text-align: left;
  }

  .dash-header__dateline {
    flex-direction: column;
    gap: 0.35rem;
    text-align: center;
  }

  .panel__header {
    flex-direction: row;
    gap: 1rem;
  }

  .readiness-header {
    justify-content: flex-start;
  }

  .field__input--narrow {
    min-width: 100px;
    flex: 1;
  }

  .jobs__actions {
    flex-direction: column;
  }

  .jobs__actions .btn {
    width: 100%;
  }

  .queue-counts {
    gap: 1.25rem;
  }

  .queue-count__num {
    font-size: 1.5rem;
  }
}

@media (max-width: 400px) {
  .panel {
    padding: 2rem 1rem;
  }

  .dash-header {
    padding: 1.5rem 1rem 0;
  }

  .section-number {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1.1rem;
  }

  .jobs__body {
    padding: 1rem;
  }
}
</style>
